import { useState, type JSX } from 'react';
import { EMPTY, useEngine } from '../../state/engineStore';
import type { FaultSnapshot } from '../../engine/faults/faults';
import type { InterlockSnapshot } from '../../engine/interlocks/interlocks';
import type { SequenceSnapshot } from '../../engine/control/sequence';

export function SandboxPanel(): JSX.Element {
  const faults = useEngine((s) => s.snap?.faults ?? (EMPTY as unknown as FaultSnapshot[]));
  const interlocks = useEngine((s) => s.snap?.interlocks ?? (EMPTY as unknown as InterlockSnapshot[]));
  const sequences = useEngine((s) => s.snap?.sequences ?? (EMPTY as unknown as SequenceSnapshot[]));
  const esd = useEngine((s) => s.snap?.esdActive ?? false);
  const send = useEngine((s) => s.send);
  const [bypassing, setBypassing] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  return (
    <div className="scroll" style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, alignItems: 'start' }}>
      <section>
        <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Fault injection</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {faults.map((f) => (
            <div
              key={f.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 9px',
                background: 'var(--panel)',
                border: `1px solid ${f.active ? 'var(--pri-high)' : 'var(--line-dim)'}`,
                borderRadius: 4,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12 }}>{f.name}</div>
                <div className="muted" style={{ fontSize: 10.5 }}>
                  {f.description}
                </div>
                <div className="muted num" style={{ fontSize: 10 }}>
                  {f.kind} → {f.target}
                </div>
              </div>
              <button
                className={f.active ? 'on' : ''}
                onClick={() => send(f.active ? { t: 'clearFault', id: f.id } : { t: 'injectFault', id: f.id })}
              >
                {f.active ? 'Clear' : 'Inject'}
              </button>
            </div>
          ))}
        </div>
      </section>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <section>
          <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Interlocks and trips</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {interlocks.map((i) => (
              <div
                key={i.id}
                style={{
                  padding: '7px 9px',
                  background: 'var(--panel)',
                  border: `1px solid ${i.tripped || i.latched ? 'var(--pri-emergency)' : i.bypassed ? 'var(--pri-medium)' : 'var(--line-dim)'}`,
                  borderRadius: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 12, flex: 1 }}>{i.name}</strong>
                  <span
                    className="num"
                    style={{
                      fontSize: 10.5,
                      color: i.latched ? 'var(--pri-emergency)' : i.bypassed ? 'var(--pri-medium)' : 'var(--text-mute)',
                    }}
                  >
                    {i.bypassed ? 'BYPASSED' : i.latched ? 'LATCHED' : i.tripped ? 'TRIPPED' : 'HEALTHY'}
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 10.5, marginTop: 3, lineHeight: 1.4 }}>
                  {i.description}
                </div>
                {i.firstOutText && (
                  <div style={{ fontSize: 11, marginTop: 4, color: 'var(--pri-high)' }}>
                    First out: {i.firstOutText}
                  </div>
                )}
                {i.bypassReason && (
                  <div className="muted" style={{ fontSize: 10.5, marginTop: 3 }}>
                    Bypass reason: {i.bypassReason}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    disabled={!i.resetAvailable}
                    title={i.resetAvailable ? 'Reset the latch' : 'The cause is still present, or the reset permissive is not met'}
                    onClick={() => send({ t: 'resetInterlock', id: i.id })}
                  >
                    Reset
                  </button>
                  {i.bypassed ? (
                    <button onClick={() => send({ t: 'bypassInterlock', id: i.id, on: false, reason: '' })}>
                      Remove bypass
                    </button>
                  ) : (
                    <button disabled={!i.resetAvailable && !i.causeActive && false} onClick={() => setBypassing(i.id)}>
                      Bypass…
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Sequences</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {sequences.map((s) => (
              <div key={s.id} style={{ padding: '7px 9px', background: 'var(--panel)', border: '1px solid var(--line-dim)', borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 12, flex: 1 }}>{s.name}</strong>
                  <span className="muted num" style={{ fontSize: 10.5 }}>
                    {s.running ? `${s.stateName} · step ${s.stepIndex + 1}/${s.totalSteps}` : 'Idle'}
                  </span>
                </div>
                {s.running && (
                  <>
                    <div style={{ height: 4, background: 'var(--bg-deep)', borderRadius: 2, margin: '6px 0 4px' }}>
                      <div style={{ width: `${((s.stepIndex + 1) / s.totalSteps) * 100}%`, height: '100%', background: 'var(--info)', borderRadius: 2 }} />
                    </div>
                    <div className="muted" style={{ fontSize: 10.5 }}>
                      {s.prompt}
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  {s.running ? (
                    <button onClick={() => send({ t: 'abortSequence', id: s.id })}>Abort</button>
                  ) : (
                    <button
                      disabled={!s.permissiveMet}
                      title={s.permissiveMet ? 'Start' : 'Start permissive not met'}
                      onClick={() => {
                        const [id, arg] = s.id.split(':');
                        send({ t: 'startSequence', id: id as string, arg });
                      }}
                    >
                      Start
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>Emergency shutdown</h3>
          <button
            style={{
              width: '100%',
              padding: '12px 0',
              background: esd ? 'var(--pri-emergency)' : 'var(--panel)',
              borderColor: 'var(--pri-emergency)',
              color: esd ? '#fff' : 'var(--pri-emergency)',
              fontWeight: 700,
              letterSpacing: 1.5,
            }}
            onClick={() => {
              if (confirm('Trip the entire plant to its safe state?')) send({ t: 'esd' });
            }}
          >
            {esd ? 'ESD ACTIVE' : 'PLANT ESD'}
          </button>
        </section>
      </div>

      {bypassing && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'grid', placeItems: 'center', zIndex: 500 }}
          onClick={() => setBypassing(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--panel)', border: '1px solid var(--pri-medium)', borderRadius: 5, padding: 16, width: 460 }}
          >
            <h3 style={{ margin: '0 0 8px' }}>Bypass {bypassing}</h3>
            <p style={{ marginTop: 0, fontSize: 12, color: 'var(--pri-medium)' }}>
              Bypassing a protective function removes it. The reason is mandatory and is written to
              the event log with a timestamp. Do not do this without an authorised permit.
            </p>
            <input
              autoFocus
              placeholder="Reason and permit number"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ width: '100%', marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setBypassing(null)}>Cancel</button>
              <button
                disabled={reason.trim().length < 6}
                onClick={() => {
                  send({ t: 'bypassInterlock', id: bypassing, on: true, reason: reason.trim() });
                  setReason('');
                  setBypassing(null);
                }}
              >
                Bypass
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
